
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
// Leaflet's stylesheet is loaded from the CDN in index.html, and the default
// marker icons resolve against it. Importing the bundled copy here would add a
// second stylesheet whose image paths webpack rewrites, which is how the
// markers go missing - so take L only, exactly as the free-zone screen does.
import L from 'leaflet'
import { createUserRoute } from '@/api/userRoutes'
import { AdminApi } from '@/wwapi/apis/admin-api'

const ISTANBUL: [number, number] = [41.015137, 28.97953]

@Component({ name: 'RouteFormDialog' })
export default class extends Vue {
  @Prop({ default: false }) private visible!: boolean
  // When set, the route is created on this account and the user picker is
  // hidden. Left empty on the moderation page, where the account is chosen.
  @Prop({ default: '' }) private targetUserId!: string

  private innerVisible = false
  private saving = false
  private activePoint: 'start' | 'end' = 'start'

  private userOptions: Array<{ userId: string, label: string }> = []
  private userSearchLoading = false

  private map: any = null
  private startMarker: any = null
  private endMarker: any = null
  private line: any = null

  private form: any = {
    userId: '',
    title: '',
    description: '',
    visibility: 'private',
    startAddress: '',
    endAddress: '',
    startLatitude: '',
    startLongitude: '',
    endLatitude: '',
    endLongitude: ''
  }

  private get rules() {
    const coord = (min: number, max: number) =>
      (_r: any, value: any, cb: Function) => {
        const n = Number(value)
        if (value === '' || value === null || isNaN(n) || n < min || n > max) {
          cb(new Error(this.$t('savedRoutes.coordInvalid') as string))
          return
        }
        cb()
      }
    return {
      userId: [{ required: true, message: this.$t('savedRoutes.userRequired'), trigger: 'change' }],
      title: [{ required: true, message: this.$t('savedRoutes.titleRequired'), trigger: 'blur' }],
      startLatitude: [{ validator: coord(-90, 90), trigger: 'blur' }],
      startLongitude: [{ validator: coord(-180, 180), trigger: 'blur' }],
      endLatitude: [{ validator: coord(-90, 90), trigger: 'blur' }],
      endLongitude: [{ validator: coord(-180, 180), trigger: 'blur' }]
    }
  }

  @Watch('visible')
  private onVisibleChange(v: boolean) {
    this.innerVisible = v
  }

  @Watch('innerVisible')
  private onInnerChange(v: boolean) {
    this.$emit('update:visible', v)
  }

  // Built here rather than in a lifecycle hook: the container only exists once
  // the dialog has finished opening, and Leaflet throws if handed one that is
  // not in the DOM yet.
  private onOpened() {
    const el = this.$refs.mapEl as HTMLElement
    if (!el) return

    if (!this.map) {
      this.map = L.map(el).setView(ISTANBUL, 11)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '',
        maxZoom: 19
      }).addTo(this.map)

      this.startMarker = L.marker(ISTANBUL, { draggable: true })
        .addTo(this.map)
        .on('dragend', () => this.readMarker('start'))
      this.endMarker = L.marker(ISTANBUL, { draggable: true })
        .addTo(this.map)
        .on('dragend', () => this.readMarker('end'))
      this.line = L.polyline([], { weight: 3, dashArray: '5,6' }).addTo(this.map)

      this.map.on('click', (e: any) => {
        const marker = this.activePoint === 'start' ? this.startMarker : this.endMarker
        marker.setLatLng(e.latlng)
        this.readMarker(this.activePoint)
        // Placing the start point moves focus to the end, so two clicks
        // produce a whole route.
        if (this.activePoint === 'start') this.activePoint = 'end'
      })
    }

    // The dialog animates open, so the container had no size while the map was
    // being built; without this the tiles render into a zero-height box.
    this.$nextTick(() => this.map && this.map.invalidateSize())
    this.syncMarkers()
  }

  private onClosed() {
    this.resetForm()
  }

  private readMarker(which: 'start' | 'end') {
    const marker = which === 'start' ? this.startMarker : this.endMarker
    const { lat, lng } = marker.getLatLng()
    this.form[which + 'Latitude'] = lat.toFixed(6)
    this.form[which + 'Longitude'] = lng.toFixed(6)
    this.drawLine()
  }

  // Typed coordinates win: move the markers to wherever the inputs say.
  private syncMarkers() {
    if (!this.map) return
    const place = (which: 'start' | 'end', marker: any) => {
      const lat = Number(this.form[which + 'Latitude'])
      const lng = Number(this.form[which + 'Longitude'])
      if (this.form[which + 'Latitude'] === '' || isNaN(lat) || isNaN(lng)) return null
      marker.setLatLng([lat, lng])
      return [lat, lng] as [number, number]
    }
    const s = place('start', this.startMarker)
    const e = place('end', this.endMarker)
    this.drawLine()
    const shown = [s, e].filter(Boolean) as Array<[number, number]>
    if (shown.length === 2) this.map.fitBounds(shown, { padding: [40, 40] })
    else if (shown.length === 1) this.map.setView(shown[0], 14)
  }

  private drawLine() {
    if (!this.line) return
    this.line.setLatLngs([this.startMarker.getLatLng(), this.endMarker.getLatLng()])
  }

  private async remoteSearchUsers(query: string) {
    const q = (query || '').trim()
    if (q.length < 2) {
      this.userOptions = []
      return
    }
    this.userSearchLoading = true
    try {
      const adminApi = new AdminApi()
      const res: any = await adminApi.userListPost({
        isPremium: null,
        isAssistant: null,
        loginType: null,
        userType: null,
        text: q,
        sort: '+id',
        page: 1,
        limit: 20
      } as any)
      const users: any[] = res.data?.data || []
      this.userOptions = users
        .filter((u) => u.userId || u.user_id)
        .map((u) => {
          const userId = u.userId || u.user_id
          const name = [u.firstName || u.first_name, u.lastName || u.last_name]
            .filter(Boolean).join(' ').trim()
          return { userId, label: name ? `${name} (${u.email})` : u.email }
        })
    } catch (err) {
      this.userOptions = []
    } finally {
      this.userSearchLoading = false
    }
  }

  private submit() {
    (this.$refs.form as any).validate(async(valid: boolean) => {
      if (!valid) return
      const userId = this.targetUserId || this.form.userId
      if (!userId) return

      this.saving = true
      try {
        await createUserRoute(userId, {
          title: this.form.title.trim(),
          description: this.form.description.trim(),
          visibility: this.form.visibility,
          startAddress: this.form.startAddress.trim(),
          endAddress: this.form.endAddress.trim(),
          startLatitude: Number(this.form.startLatitude),
          startLongitude: Number(this.form.startLongitude),
          endLatitude: Number(this.form.endLatitude),
          endLongitude: Number(this.form.endLongitude)
        })
        this.$message.success(this.$t('savedRoutes.saved') as string)
        this.innerVisible = false
        this.$emit('saved')
      } catch (err: any) {
        this.$message.error(err?.message || (this.$t('savedRoutes.saveFailed') as string))
      } finally {
        this.saving = false
      }
    })
  }

  private resetForm() {
    this.form = {
      userId: '',
      title: '',
      description: '',
      visibility: 'private',
      startAddress: '',
      endAddress: '',
      startLatitude: '',
      startLongitude: '',
      endLatitude: '',
      endLongitude: ''
    }
    this.activePoint = 'start'
    this.userOptions = []
    const f = this.$refs.form as any
    if (f) f.clearValidate()
  }
}
